// Browser side of Google sign-in (B8 contract: docs/api/AUTH-GOOGLE.md, pinned in the round 10 briefs).
// The browser never talks to Google: "Continue with Google" is a plain link to the API's start route,
// which redirects to Google and back through the API's callback. A failed callback lands on
// /sign-in?error=google_<code>; the copy for each code lives here. Nothing here logs or stores a value.

export const GOOGLE_ERROR_CODES = ['not_configured', 'state_mismatch', 'provider_error', 'email_unverified', 'domain_not_allowed', 'user_disabled'] as const;
export type GoogleErrorCode = (typeof GOOGLE_ERROR_CODES)[number];

/** Where the callback sends a failed sign-in. Not a workspace route: the sign-in screen moves it to "/". */
export const SIGN_IN_PATH = '/sign-in';

export interface GoogleProvider {
  enabled: boolean;
  /** Lower-case domains without the "@", as the server reports them. */
  allowedDomains: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const DOMAIN = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function isDomain(value: string): boolean {
  return DOMAIN.test(value);
}

/** Lower-cases, drops a leading "@" and keeps only well-formed, distinct domains. */
export function cleanDomains(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const domains = values.filter((value): value is string => typeof value === 'string').map(value => value.trim().toLowerCase().replace(/^@/, ''));
  return [...new Set(domains.filter(isDomain))];
}

/**
 * GET /auth/providers: `{google: {enabled, allowed_domains}}`, bare or inside `{data}`. Anything
 * else (an older server's 404 body, a malformed answer) reads as null, which hides Google.
 */
export function readProviders(body: unknown): GoogleProvider | null {
  const root = isRecord(body) && isRecord(body.data) ? body.data : body;
  if (!isRecord(root) || !isRecord(root.google) || typeof root.google.enabled !== 'boolean') return null;
  return { enabled: root.google.enabled, allowedDomains: cleanDomains(root.google.allowed_domains) };
}

/** "@brandcore.io", "@a.com or @b.com", "@a.com, @b.com, or @c.com". */
export function domainList(domains: readonly string[], type: 'disjunction' | 'conjunction' = 'disjunction'): string {
  return new Intl.ListFormat('en', { style: 'long', type }).format(domains.map(domain => `@${domain}`));
}

/** The hint under the Google action. */
export function googleHint(provider: GoogleProvider): string {
  return provider.allowedDomains.length ? `Use your ${domainList(provider.allowedDomains)} account.` : 'Use the Google account your invitation was sent to.';
}

/**
 * Where Google should bring this person back: the page they asked for, never the sign-in landing
 * itself, never another origin, never the error that brought them here, and never a fragment
 * (invitation and recovery tokens live there). The server checks it again.
 */
export function signInReturnPath(path: string, query: string): string {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return '/';
  if (path === SIGN_IN_PATH || path.startsWith(`${SIGN_IN_PATH}/`)) return '/';
  const params = new URLSearchParams(query);
  params.delete('error');
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
}

/** The `error` query value after a failed Google callback: a known code, 'unknown' for another google_ code, else null. */
export function googleErrorCode(param: string | null): GoogleErrorCode | 'unknown' | null {
  if (!param?.startsWith('google_')) return null;
  const code = param.slice('google_'.length);
  return (GOOGLE_ERROR_CODES as readonly string[]).includes(code) ? (code as GoogleErrorCode) : 'unknown';
}

export interface GoogleErrorView {
  code: GoogleErrorCode | 'unknown';
  /** What happened, in a sentence. */
  title: string;
  /** Why, and what to do next. */
  detail: string;
}

/**
 * Plain copy for each callback failure. `domains` is the allowed list from GET /auth/providers;
 * null while it hasn't loaded, so the domain sentence never names a guess.
 */
export function googleErrorCopy(code: GoogleErrorCode | 'unknown', domains: readonly string[] | null): GoogleErrorView {
  const view = (title: string, detail: string): GoogleErrorView => ({ code, title, detail });
  switch (code) {
    case 'not_configured':
      return view('Google sign-in isn’t available for this workspace right now.', 'An admin needs to set it up or turn it back on. If you have a password for this workspace, sign in with it below.');
    case 'state_mismatch':
      return view('That Google sign-in expired before it finished.', 'It took too long, or it started in another tab or browser. Choose Continue with Google to start again.');
    case 'provider_error':
      return view('Google didn’t complete the sign-in.', 'You may have canceled on Google’s page, or Google had a problem. Choose Continue with Google to try again.');
    case 'email_unverified':
      return view(
        'Google hasn’t verified the email address on that account.',
        domains?.length ? `Choose Continue with Google and pick your ${domainList(domains)} account.` : 'Verify the address with Google, then choose Continue with Google again.',
      );
    case 'domain_not_allowed':
      return view(
        'That Google account can’t sign in to this workspace.',
        domains === null
          ? 'This workspace doesn’t accept that account. Ask an admin for an invitation.'
          : domains.length
            ? `This workspace accepts ${domainList(domains)} accounts. Ask an admin for an invitation.`
            : 'This workspace accepts invited accounts only. Ask an admin for an invitation.',
      );
    case 'user_disabled':
      return view('Your account is deactivated in this workspace.', 'An admin turned off your access. Ask an admin to reactivate your account, then sign in again.');
    default:
      return view('Google sign-in didn’t finish.', 'Choose Continue with Google to try again. If it keeps failing, ask an admin for help.');
  }
}

/**
 * B8 decision (docs/DECISIONS.md): a Google sign-in satisfies the second factor, because Google
 * Workspace enforces its own 2-step verification; password accounts still need an authenticator code.
 */
export function hasSecondFactor(authMethods: readonly string[]): boolean {
  return authMethods.includes('totp') || authMethods.includes('google');
}

/** The Google "G" on the 24 px grid, drawn as strokes for ui/Icon.tsx PathIcon (currentColor, no image). */
export const GOOGLE_MARK = ['M20 12a8 8 0 1 1-2.34-5.66', 'M20 12h-7.5'] as const;
